/**
 * RoutineParamRows — repeatable parameter row editor for the routine form.
 * Extracted to keep the form orchestrator under the 200-line CLAUDE.md
 * budget.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7.
 */
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import {
  PARAM_MODE_OPTIONS,
  type RoutineParamFormValue,
} from './routineFormHelpers';

/**
 * Radix Select reserves `''`, so the `PARAM_MODE_OPTIONS` "IN (default)" entry
 * — whose `''` value means "omit the mode keyword" to `paramFromForm` — is
 * carried through the Select as this sentinel and mapped back to `''` on the
 * way out.
 */
const MODE_UNSET = '__unset__' as const;

export interface RoutineParamRowsProps {
  readonly engine: SqlEngine;
  readonly params: ReadonlyArray<RoutineParamFormValue>;
  readonly onChange: (next: ReadonlyArray<RoutineParamFormValue>) => void;
  readonly testId: string;
  /** When the routine is a function some engines reject OUT/INOUT. */
  readonly modeAllowed: boolean;
}

export function RoutineParamRows({
  engine, params, onChange, testId, modeAllowed,
}: RoutineParamRowsProps) {
  const update = (idx: number, patch: Partial<RoutineParamFormValue>) => {
    onChange(params.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const remove = (idx: number) => {
    onChange(params.filter((_, i) => i !== idx));
  };
  const append = () => {
    onChange([...params, { name: '', typeText: '', mode: '', defaultExpr: '' }]);
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium">Parameters</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-2 text-[11px]"
          onClick={append}
          data-testid={`${testId}-add`}
        >
          <Plus className="h-3 w-3" />
          Add
        </Button>
      </div>
      {params.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-border px-2 py-2 text-center text-[11px] text-muted-foreground"
          data-testid={`${testId}-empty`}
        >
          No parameters — click Add to append one.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {params.map((p, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr,1fr,minmax(80px,auto),minmax(80px,auto),auto] gap-1.5 items-center"
              data-testid={`${testId}-row-${idx}`}
            >
              <Input
                className="h-7 text-xs"
                placeholder="name"
                value={p.name}
                data-testid={`${testId}-name-${idx}`}
                onChange={(e) => update(idx, { name: e.target.value })}
              />
              <Input
                className="h-7 font-mono text-xs"
                placeholder="TYPE"
                value={p.typeText}
                data-testid={`${testId}-type-${idx}`}
                onChange={(e) => update(idx, { typeText: e.target.value })}
              />
              <Select
                value={p.mode === '' ? MODE_UNSET : p.mode}
                disabled={!modeAllowed}
                onValueChange={(v) =>
                  update(idx, {
                    mode: (v === MODE_UNSET ? '' : v) as RoutineParamFormValue['mode'],
                  })
                }
              >
                <SelectTrigger
                  className="h-7 px-2 py-0 text-[11px]"
                  data-testid={`${testId}-mode-${idx}`}
                  disabled={!modeAllowed}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARAM_MODE_OPTIONS.map((o) => (
                    <SelectItem key={o.value || '_default'} value={o.value || MODE_UNSET}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="h-7 font-mono text-xs"
                placeholder={engine === 'mysql' ? '— mysql —' : 'DEFAULT'}
                value={p.defaultExpr}
                data-testid={`${testId}-default-${idx}`}
                disabled={engine === 'mysql'}
                onChange={(e) => update(idx, { defaultExpr: e.target.value })}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-1"
                onClick={() => remove(idx)}
                data-testid={`${testId}-remove-${idx}`}
                aria-label={`Remove parameter ${idx + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
